import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { configSettings } from '@/lib/config';
import { User } from '@/types/user';

interface ClarityContextValue {
    identifyInClarity: (user: User) => void;
    setTag: (key: string, value: string | number | boolean) => void;
    setTags: (tags: Record<string, string | number | boolean>) => void;
    setExperiments: (experiments: Record<string, any>) => void;
    setDaysFromSignup: (createdAt: number) => void;
    upgrade: (reason?: string) => void;
    isReady: () => boolean;
}

const ClarityContext = createContext<ClarityContextValue | null>(null);

/**
 * Provider that initializes Microsoft Clarity and provides tracking functions
 */
export function ClarityProvider({ children }: { children: React.ReactNode }) {
    // Initialize Clarity once on mount
    useEffect(() => {
        if (!configSettings.clarityTrackingCode || window.clarity) return;

        try {
            /* eslint-disable */
            (function (
                c: any,
                l: Document,
                a: string,
                r: string,
                i: string,
                t: HTMLScriptElement | null,
                y: Element | undefined
            ) {
                c[a] =
                    c[a] ||
                    function () {
                        (c[a].q = c[a].q || []).push(arguments);
                    };
                t = l.createElement(r) as HTMLScriptElement;
                t.async = true;
                t.src = 'https://www.clarity.ms/tag/' + i;
                y = l.getElementsByTagName(r)[0];
                if (y && y.parentNode) {
                    y.parentNode.insertBefore(t, y);
                }
            })(window, document, 'clarity', 'script', configSettings.clarityTrackingCode, null, undefined);
            /* eslint-enable */
        } catch (error) {
            console.error('Failed to load Clarity script:', error);
        }
    }, []);

    const identifyInClarity = useCallback((user: User) => {
        if (!user || !window.clarity) return;

        try {
            window.clarity('identify', String(user.user_id));
            window.clarity('set', 'user_id', String(user.user_id));
            window.clarity('set', 'username', user.username);
        } catch (error) {
            console.error('Failed to identify user in Clarity:', error);
        }
    }, []);

    const setTag = useCallback((key: string, value: string | number | boolean) => {
        if (!window.clarity) return;

        try {
            window.clarity('set', key, String(value));
        } catch (error) {
            console.error('Failed to set Clarity tag:', error);
        }
    }, []);

    const setTags = useCallback((tags: Record<string, string | number | boolean>) => {
        if (!window.clarity) return;

        try {
            Object.entries(tags).forEach(([key, value]) => {
                window.clarity!('set', key, String(value));
            });
        } catch (error) {
            console.error('Failed to set Clarity tags:', error);
        }
    }, []);

    const setExperiments = useCallback((experiments: Record<string, any>) => {
        if (!window.clarity || !experiments) return;

        try {
            Object.entries(experiments).forEach(([experiment, value]) => {
                window.clarity!('set', `experiment-${experiment}`, String(value));
            });
        } catch (error) {
            console.error('Failed to set Clarity experiments:', error);
        }
    }, []);

    const setDaysFromSignup = useCallback((createdAt: number) => {
        if (!window.clarity) return;

        try {
            const diffTime = Math.abs(Date.now() - createdAt * 1000);
            const msInDay = 1000 * 60 * 60 * 24;
            const daysFromSignup = Math.ceil(diffTime / msInDay);

            window.clarity('set', 'days_from_signup', String(daysFromSignup));
        } catch (error) {
            console.error('Failed to set days from signup in Clarity:', error);
        }
    }, []);

    const upgrade = useCallback((reason?: string) => {
        if (!window.clarity) return;

        try {
            if (reason) {
                window.clarity('upgrade', reason);
            } else {
                window.clarity('upgrade');
            }
        } catch (error) {
            console.error('Failed to upgrade Clarity session:', error);
        }
    }, []);

    const isReady = useCallback(() => {
        return typeof window.clarity !== 'undefined';
    }, []);

    const value = useMemo<ClarityContextValue>(
        () => ({
            identifyInClarity,
            setTag,
            setTags,
            setExperiments,
            setDaysFromSignup,
            upgrade,
            isReady,
        }),
        [identifyInClarity, setTag, setTags, setExperiments, setDaysFromSignup, upgrade, isReady]
    );

    return <ClarityContext.Provider value={value}>{children}</ClarityContext.Provider>;
}

/**
 * Hook to access Clarity functions
 * Must be used within ClarityProvider
 */
export function useClarity() {
    const context = useContext(ClarityContext);
    if (!context) {
        throw new Error('useClarity must be used within ClarityProvider');
    }
    return context;
}
